import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '@/firebase/firebase';
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { useAuth } from '@/context/AuthContext';
import { ExclamationTriangleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * TeamSetupNotice
 * - Shows a warning until team.setupComplete === true
 * - Lists missing fields
 * - Allows "Mark as complete" when nothing is missing
 *
 * Props:
 *  - team: { id, name, sportType, ageGroup, homeCity/city, region/state, branding, logoUrl, setupComplete }
 *
 * primaryLeague used to be a required field here. Dropped from the
 * required-set when we removed the League section from the wizard;
 * it's still capturable from EditTeam, just not gated on completion.
 */
export default function TeamSetupNotice({ team }) {
  const { firebaseUser } = useAuth();
  const [saving, setSaving] = useState(false);

  // Required set aligned with TeamSetupWizard's allComplete (July 2026):
  // basics + primary color only. Location is optional (weather, a
  // post-activation concern) and logo was already optional in the wizard —
  // this notice used to demand BOTH, so it could nag forever about fields
  // the wizard never required. Keep the two definitions in lockstep.
  const missing = useMemo(() => {
    if (!team) return ['Team not loaded'];
    const fields = [];
    if (!team.name) fields.push('Team Name');
    if (!team.sportType) fields.push('Sport Type');
    if (!team.ageGroup) fields.push('Age Group');
    const primaryColor = team.branding?.primaryColor || team.primaryColor;
    if (!primaryColor) fields.push('Primary Color');
    return fields;
  }, [team]);

  const canMarkComplete = !missing.length;

  async function markComplete() {
    if (!team?.id || !firebaseUser?.uid) return;
    if (!canMarkComplete) {
      toast.error('Please complete the required fields below first.');
      return;
    }
    try {
      setSaving(true);
      await updateDoc(doc(db, 'teams', team.id), {
        setupComplete: true,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        mustCompleteTeamId: deleteField(),
        updatedAt: serverTimestamp(),
      });
      toast.success('Team setup completed!');
      // rely on parent to refresh team state after save
    } catch (e) {
      console.error('[TeamSetupNotice] mark complete failed', e);
      toast.error('Could not mark complete');
    } finally {
      setSaving(false);
    }
  }

  if (!team || team.setupComplete === true) return null;

  // Two render modes:
  //   - Missing fields → amber warning card with a "Finish setup" CTA
  //     that takes the coach to the wizard at /teams/:id/setup. Lists
  //     each missing field so they know what's incomplete before they
  //     leave the team page.
  //   - All fields filled but setupComplete is still false (rare —
  //     usually means the wizard finishSetup callback never fired) →
  //     lime "Mark as complete" affordance so the coach can clear the
  //     reminder without re-running the wizard.
  return (
    <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3.5">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100">
          <ExclamationTriangleIcon className="h-4 w-4 text-amber-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="text-[12px] font-bold uppercase tracking-[1.2px] text-amber-800"
            style={{ fontFamily: HEAD_FONT }}
          >
            Finish setting up your team
          </p>
          <p className="mt-1 text-xs text-amber-900/80">
            {canMarkComplete
              ? 'All set, just confirm to mark setup complete.'
              : 'A few basics left so your team pages and messages display right.'}
          </p>
          {missing.length > 0 && (
            <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-amber-900/80 list-disc pl-5">
              {missing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {canMarkComplete ? (
              <button
                type="button"
                onClick={markComplete}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1.5 text-xs font-bold uppercase transition-colors disabled:opacity-60"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {saving ? 'Saving…' : 'Mark as complete'}
              </button>
            ) : (
              <Link
                to={`/teams/${team.id}/setup`}
                className="inline-flex items-center gap-1.5 rounded-[10px] bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                Finish setup
                <ArrowRightIcon className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
